import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog, AbstractFormDialogData } from '@next/core-lib/dialog';
import { SelectedLocation } from '@next/core-lib/user';
import { CropFieldLink, LinkCropFieldData, CropFieldLinkTypesEnum } from 'src/app/shared/models';

export interface LinkCropfieldDialogData extends AbstractFormDialogData {
  location: SelectedLocation;
  cropFieldLinks: CropFieldLink[];
  locations: SelectedLocation[];
  shipTosWithOrders: number[];
  cropFieldLinkType: CropFieldLinkTypesEnum;
}

@Component({
  selector: 'app-link-cropfield-dialog',
  templateUrl: './link-cropfield-dialog.component.html',
  styleUrls: ['./link-cropfield-dialog.component.scss'],
  standalone: false,
})

export class LinkCropfieldDialogComponent extends AbstractFormDialog<LinkCropFieldData> implements OnInit {
  data = inject<LinkCropfieldDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<LinkCropfieldDialogComponent, AbstractDialogResultData<LinkCropFieldData>>>(MatDialogRef);

  formGroupByCropFieldId: { [cropFieldId: number]: FormGroup } = this.data.cropFieldLinks.reduce(
    (a, c) => ({
      ...a,
      [c.id]: this.createCropFieldFormGroup({
        ...c,
        linkedToThisLocation:
          this.data.cropFieldLinkType === CropFieldLinkTypesEnum.presales
            ? c.shipToId === this.data.location.id
            : c.fertilizerShipToId === this.data.location.id,
      }),
    }),
    {},
  );
  formArray = this.formBuilder.array(Object.values(this.formGroupByCropFieldId));
  formGroup = this.formBuilder.group({
    cropFields: this.formArray,
  });
  searchControl = new FormControl();
  displayedColumns = ['cropField', 'linkedToLocation', 'locationExternalId'];
  masterToggleSelected = false;
  shipToProperty = this.data.cropFieldLinkType === CropFieldLinkTypesEnum.presales ? 'shipToId' : 'fertilizerShipToId';

  ngOnInit(): void {
    this.searchControl.valueChanges.subscribe((value: string) => {
      if (!value || value === '') {
        Object.values(this.formGroupByCropFieldId).forEach((cropFieldLink) => {
          cropFieldLink.get('hidden').setValue(false);
        });
      } else {
        this.data.cropFieldLinks.forEach((cropFieldLink) => {
          const matchByFilter =
            cropFieldLink.name.toLowerCase().includes(value.toLowerCase()) || cropFieldLink.cropName.toLowerCase().includes(value.toLowerCase());

          this.formGroupByCropFieldId[cropFieldLink.id].get('hidden').setValue(!matchByFilter);
        });
      }
      this.updateMasterToggle();
    });

    this.updateMasterToggle();
  }

  resetFilter() {
    this.searchControl.setValue(null);
  }

  masterToggle() {
    if (
      this.getVisibleItems()
        .filter((link) => !this.isCropFieldLinkBlocked(link.get(this.shipToProperty)?.value))
        .some((link) => !link.get('linkedToThisLocation').value)
    ) {
      //Select all
      this.setAllCropFieldLinks(true);
      this.masterToggleSelected = true;
    } else {
      //Deselect all
      this.setAllCropFieldLinks(false);
      this.masterToggleSelected = false;
    }
  }

  updateMasterToggle() {
    this.masterToggleSelected = !this.getVisibleItems().some(
      (link) => !this.isCropFieldLinkBlocked(link.get(this.shipToProperty)?.value) && !link.get('linkedToThisLocation').value,
    );
  }

  setAllCropFieldLinks(linked: boolean) {
    this.data.cropFieldLinks.forEach((link) => {
      if (
        !this.isCropFieldLinkBlocked(this.data.cropFieldLinkType === CropFieldLinkTypesEnum.presales ? link.shipToId : link.fertilizerShipToId) &&
        !this.formGroupByCropFieldId[link.id].get('hidden').value
      ) {
        link.linkedToThisLocation = linked;
        this.formGroupByCropFieldId[link.id].get('linkedToThisLocation').setValue(linked);
      }
    });
  }

  getOtherLocationName = (locationId?: number): string =>
    locationId && locationId !== this.data.location.id ? this.data.locations.find((l) => l.id === locationId)?.name : '';

  getOtherLocationExternalId = (locationId?: number): string =>
    locationId && locationId !== this.data.location.id ? this.data.locations.find((l) => l.id === locationId)?.externalId : '';

  hasLocationPlacedOrder = (locationId?: number): boolean =>
    locationId && this.data.location.id !== locationId && this.data.shipTosWithOrders.includes(locationId);

  toggleLinkedToThisLocation() {
    this.updateMasterToggle();
  }

  isAnyRowVisible = (): boolean =>
    Object.entries(this.formGroupByCropFieldId)
      .map(([_, value]) => value)
      .some((row) => !row.get('hidden').value);

  getVisibleItems = (): FormGroup[] =>
    Object.entries(this.formGroupByCropFieldId)
      .map(([_, val]) => val)
      .filter((item) => !item.get('hidden').value);

  confirm($event: { cropFields: CropFieldLink[] }) {
    const cropFields = $event.cropFields.map((cropField) => ({
      ...cropField,
      shipToId: this.setCropFieldShipToIdOnConfirm(cropField),
    }));
    super.confirm({ cropFields });
  }

  setCropFieldShipToIdOnConfirm(cropField: CropFieldLink): number | null {
    if (cropField.linkedToThisLocation) {
      return this.data.location.id;
    } else {
      if (this.data.cropFieldLinkType === CropFieldLinkTypesEnum.presales && cropField.shipToId !== this.data.location.id) {
        return cropField.shipToId;
      } else if (this.data.cropFieldLinkType === CropFieldLinkTypesEnum.fertilizer && cropField.fertilizerShipToId !== this.data.location.id) {
        return cropField.fertilizerShipToId;
      } else {
        return null;
      }
    }
  }

  isCropFieldLinkBlocked(shipToId?: number): boolean {
    return !!(shipToId &&
      this.data.location.id !== shipToId &&
      this.data.shipTosWithOrders.includes(shipToId));
  }

  private createCropFieldFormGroup(c: CropFieldLink) {
    const formGroup = this.formBuilder.group({
      id: c.id,
      name: c.name,
      cropName: c.cropName,
      linkedToThisLocation: c.linkedToThisLocation,
      hidden: false,
      shipToId: this.data.cropFieldLinkType === CropFieldLinkTypesEnum.presales ? c.shipToId : c.fertilizerShipToId,
    });

    if (this.isCropFieldLinkBlocked(c.shipToId)) {
      formGroup.disable();
    }

    return formGroup;
  }
}
